// src/components/modules/TAI/PlanillasTAI.js

import React, { useState, useEffect, useCallback } from "react";
import {
  Layout,
  Typography,
  Card,
  Row,
  Col,
  Spin,
  message,
  Button,
  Space,
  Statistic,
  Badge,
  Select,
  DatePicker,
  Input,
  Table,
  Tag,
  Tooltip,
  Dropdown,
  Empty,
} from "antd";
import {
  ReloadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  PrinterOutlined,
  MoreOutlined,
  SearchOutlined,
  EyeOutlined,
  DownloadOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import dayjs from "../../../utils/dayjs";
import api from "../../../axiosConfig";
import { useUser } from "../../../UserContext";
import PlanillaTAICard from "./PlanillaTAICard";
import PlanillaTAIEstadoModal from "./PlanillaTAIEstadoModal";
import "./TAI.css";

const { Content } = Layout;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Colores por estado de planilla (solo 3 estados: Generada, Recibida, Facturada)
const ESTADO_COLORS = {
  generada: { color: "#8c8c8c", bg: "#fafafa", label: "Generada" },
  recibida: { color: "#52c41a", bg: "#f6ffed", label: "Recibida" },
  facturada: { color: "#722ed1", bg: "#f9f0ff", label: "Facturada" },
};

const PlanillasTAI = () => {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [planillas, setPlanillas] = useState([]);
  const [estadisticas, setEstadisticas] = useState({});
  const [total, setTotal] = useState(0);
  const [selectedPlanillas, setSelectedPlanillas] = useState([]);
  const [viewMode, setViewMode] = useState("cards"); // cards | table
  const [estadoModalOpen, setEstadoModalOpen] = useState(false);
  const [selectedPlanillaEstado, setSelectedPlanillaEstado] = useState(null);
  const [transportadoras, setTransportadoras] = useState([]);
  const [downloadLoading, setDownloadLoading] = useState(false);

  // Paginación
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // Filtros
  const [filters, setFilters] = useState({
    fechaInicio: dayjs().startOf("month").format("YYYY-MM-DD"),
    fechaFin: dayjs().endOf("month").format("YYYY-MM-DD"),
    transportadora_id: null,
    estado: null,
    searchQuery: "",
  });

  // Función para verificar permisos
  const hasPermission = useCallback(
    (permission) => {
      if (!user) return false;
      if (user.es_administrador && user.sinRol) return true;

      return user.modulosPermisos?.some(
        (mp) => mp.modulo?.toLowerCase() === "tai" && mp.permiso === permission
      );
    },
    [user]
  );

  // Cargar transportadoras
  const fetchTransportadoras = useCallback(async () => {
    try {
      const response = await api.get("/tai/transportadoras");
      if (response.data.success) {
        setTransportadoras(response.data.data);
      }
    } catch (error) {
      console.error("Error al cargar transportadoras:", error);
    }
  }, []);

  // Cargar planillas TAI
  const fetchPlanillas = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      params.append("page", pagination.current);
      params.append("per_page", pagination.pageSize);

      if (filters.fechaInicio) params.append("fecha_inicio", filters.fechaInicio);
      if (filters.fechaFin) params.append("fecha_fin", filters.fechaFin);
      if (filters.transportadora_id) params.append("transportadora_id", filters.transportadora_id);
      if (filters.estado) params.append("estado", filters.estado);
      if (filters.searchQuery) params.append("search", filters.searchQuery);

      const response = await api.get(`/planillas-tai?${params.toString()}`);

      if (response.data.success) {
        setPlanillas(response.data.data);
        setTotal(response.data.total || 0);
        setEstadisticas(response.data.estadisticas || {});
        setPagination((prev) => ({
          ...prev,
          total: response.data.total || 0,
        }));
      }
    } catch (error) {
      console.error("Error al cargar planillas TAI:", error);
      message.error("Error al cargar planillas TAI");
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchPlanillas();
    fetchTransportadoras();
  }, [fetchPlanillas, fetchTransportadoras]);

  // Manejar cambio de filtros
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    setSelectedPlanillas([]);
  };

  // Manejar cambio de fechas
  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setFilters((prev) => ({
        ...prev,
        fechaInicio: dates[0].format("YYYY-MM-DD"),
        fechaFin: dates[1].format("YYYY-MM-DD"),
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        fechaInicio: null,
        fechaFin: null,
      }));
    }
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  // Manejar selección de planillas
  const handleSelectPlanilla = (planillaId, selected) => {
    setSelectedPlanillas((prev) => {
      if (selected) {
        return [...prev, planillaId];
      } else {
        return prev.filter((id) => id !== planillaId);
      }
    });
  };

  // Seleccionar todos
  const handleSelectAll = (selected) => {
    if (selected) {
      const ids = planillas.map((p) => p.id);
      setSelectedPlanillas(ids);
    } else {
      setSelectedPlanillas([]);
    }
  };

  // Abrir modal de cambio de estado
  const handleOpenEstadoModal = (planilla) => {
    setSelectedPlanillaEstado(planilla);
    setEstadoModalOpen(true);
  };

  // Callback después de cambiar estado
  const handleEstadoSuccess = () => {
    setEstadoModalOpen(false);
    setSelectedPlanillaEstado(null);
    fetchPlanillas();
    message.success("Estado actualizado correctamente");
  };

  // Cambio de estado masivo
  const handleCambiarEstadoMasivo = async (nuevoEstado) => {
    if (selectedPlanillas.length === 0) {
      message.warning("Seleccione al menos una planilla");
      return;
    }

    try {
      const response = await api.post("/planillas-tai/cambiar-estado-masivo", {
        planilla_ids: selectedPlanillas,
        estado: nuevoEstado,
      });

      if (response.data.success) {
        message.success(`${response.data.actualizadas} planillas actualizadas`);
        if (response.data.errores > 0) {
          message.warning(`${response.data.errores} planillas no se pudieron actualizar`);
        }
        setSelectedPlanillas([]);
        fetchPlanillas();
      }
    } catch (error) {
      console.error("Error al cambiar estado:", error);
      message.error(error.response?.data?.message || "Error al cambiar estado");
    }
  };

  // Descargar planillas seleccionadas en Excel
  const handleDescargarPlanillas = async () => {
    if (selectedPlanillas.length === 0) {
      message.warning("Seleccione al menos una planilla");
      return;
    }

    setDownloadLoading(true);
    try {
      const response = await api.post("/planillas-tai/exportar", {
        ids: selectedPlanillas,
      }, {
        responseType: "blob",
      });

      // Crear descarga
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `Planillas_TAI_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => document.body.removeChild(link), 100);

      message.success("Planillas exportadas correctamente");
    } catch (error) {
      console.error("Error al descargar:", error);
      message.error("Error al descargar planillas");
    } finally {
      setDownloadLoading(false);
    }
  };

  // Descargar planillas seleccionadas en PDF
  const handleDescargarPDF = async () => {
    if (selectedPlanillas.length === 0) {
      message.warning("Seleccione al menos una planilla");
      return;
    }

    setDownloadLoading(true);
    try {
      const response = await api.post("/planillas-tai/exportar-pdf", {
        ids: selectedPlanillas,
      }, {
        responseType: "blob",
      });

      // Crear descarga
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `Planillas_TAI_${dayjs().format("YYYYMMDD_HHmmss")}.pdf`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => document.body.removeChild(link), 100);

      message.success("PDF generado correctamente");
    } catch (error) {
      console.error("Error al generar PDF:", error);
      message.error("Error al generar PDF");
    } finally {
      setDownloadLoading(false);
    }
  };

  // Columnas para vista de tabla
  const tableColumns = [
    {
      title: "Código",
      dataIndex: "codigo_planilla",
      key: "codigo_planilla",
      width: 150,
      render: (text) => (
        <Tag color="#0a2e5c" style={{ fontWeight: "bold" }}>
          {text}
        </Tag>
      ),
    },
    {
      title: "Fecha Servicio",
      dataIndex: "fecha_servicio",
      key: "fecha_servicio",
      width: 120,
      render: (text) => dayjs(text).format("DD/MM/YYYY"),
    },
    {
      title: "Paciente",
      key: "paciente",
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.paciente?.nombre}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {record.paciente?.numero_documento}
          </Text>
        </Space>
      ),
    },
    {
      title: "Transportadora",
      key: "transportadora",
      width: 180,
      render: (_, record) => record.transportadora?.nombre || "-",
    },
    {
      title: "Trayecto",
      key: "trayecto",
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 11 }}>{record.origen}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>→ {record.destino}</Text>
        </Space>
      ),
    },
    {
      title: "Tiquete",
      dataIndex: "numero_tiquete",
      key: "numero_tiquete",
      width: 100,
      render: (text) => text || "-",
    },
    {
      title: "Valor",
      dataIndex: "valor_tiquete",
      key: "valor_tiquete",
      width: 100,
      render: (text) =>
        text ? `$${parseFloat(text).toLocaleString("es-CO")}` : "-",
    },
    {
      title: "Estado",
      dataIndex: "estado",
      key: "estado",
      width: 120,
      render: (estado) => {
        const config = ESTADO_COLORS[estado] || ESTADO_COLORS.generada;
        return (
          <Tag
            color={config.color}
            style={{
              backgroundColor: config.bg,
              color: config.color,
              border: `1px solid ${config.color}`,
            }}
          >
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: "Acciones",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver detalles">
            <Button type="text" icon={<EyeOutlined />} size="small" />
          </Tooltip>
          {record.estado !== "facturada" && (
            <Tooltip title="Cambiar estado">
              <Button
                type="text"
                icon={<CheckCircleOutlined />}
                size="small"
                onClick={() => handleOpenEstadoModal(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // Calcular estadísticas para badges
  const generadas = estadisticas.generada || 0;
  const recibidas = estadisticas.recibida || 0;
  const facturadas = estadisticas.facturada || 0;

  // Obtener estados de las planillas seleccionadas
  const estadosSeleccionados = selectedPlanillas.map((id) => {
    const planilla = planillas.find((p) => p.id === id);
    return planilla?.estado;
  });

  // Verificar si todas las seleccionadas están en un estado específico
  const todasGeneradas = estadosSeleccionados.length > 0 &&
    estadosSeleccionados.every((e) => e === "generada");
  const todasRecibidas = estadosSeleccionados.length > 0 &&
    estadosSeleccionados.every((e) => e === "recibida");
  const algunaNoFacturada = estadosSeleccionados.some((e) => e !== "facturada");

  // Menú de acciones masivas (dinámico según estados)
  const accionesMasivasItems = [
    {
      key: "descargar-excel",
      label: downloadLoading ? "Descargando..." : "Descargar Excel",
      icon: <DownloadOutlined />,
      onClick: handleDescargarPlanillas,
      disabled: downloadLoading,
    },
    {
      key: "descargar-pdf",
      label: downloadLoading ? "Generando..." : "Descargar PDF",
      icon: <FilePdfOutlined />,
      onClick: handleDescargarPDF,
      disabled: downloadLoading,
    },
    // Solo mostrar opciones de cambio de estado si hay planillas que pueden cambiar
    ...(algunaNoFacturada ? [
      {
        type: "divider",
      },
      // Mostrar "Marcar como Recibidas" solo si hay planillas en estado "generada"
      ...(todasGeneradas ? [{
        key: "recibida",
        label: "Marcar como Recibidas",
        icon: <CheckCircleOutlined />,
        onClick: () => handleCambiarEstadoMasivo("recibida"),
      }] : []),
      // Mostrar "Marcar como Facturadas" solo si hay planillas en estado "recibida"
      ...(todasRecibidas ? [{
        key: "facturada",
        label: "Marcar como Facturadas",
        icon: <PrinterOutlined />,
        onClick: () => handleCambiarEstadoMasivo("facturada"),
      }] : []),
    ] : []),
  ];

  return (
    <Layout className="tai-layout">
      <Content className="tai-content">
        {/* Header */}
        <div className="tai-header">
          <div className="tai-header-title">
            <FileTextOutlined className="tai-header-icon" />
            <Title level={3} style={{ margin: 0 }}>
              Planillas TAI
            </Title>
            <Badge count={total} showZero style={{ backgroundColor: "#722ed1" }} />
          </div>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchPlanillas}
              loading={loading}
            >
              Actualizar
            </Button>
            {selectedPlanillas.length > 0 && hasPermission("CAMBIAR_ESTADO_PLANILLA") && (
              <Dropdown
                menu={{ items: accionesMasivasItems }}
                trigger={["click"]}
              >
                <Button icon={<MoreOutlined />}>
                  Acciones ({selectedPlanillas.length})
                </Button>
              </Dropdown>
            )}
          </Space>
        </div>

        {/* Estadísticas */}
        <Row gutter={[16, 16]} className="tai-stats">
          <Col xs={12} sm={8} md={8}>
            <Card size="small" className="tai-stat-card">
              <Statistic
                title="Generadas"
                value={generadas}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: "#8c8c8c" }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={8}>
            <Card size="small" className="tai-stat-card">
              <Statistic
                title="Recibidas"
                value={recibidas}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={8}>
            <Card size="small" className="tai-stat-card">
              <Statistic
                title="Facturadas"
                value={facturadas}
                prefix={<PrinterOutlined />}
                valueStyle={{ color: "#722ed1" }}
              />
            </Card>
          </Col>
        </Row>

        {/* Filtros */}
        <Card className="tai-filters" bodyStyle={{ padding: "12px 16px" }}>
          <Row gutter={[16, 12]} align="middle">
            <Col xs={24} sm={12} md={6}>
              <RangePicker
                value={[
                  filters.fechaInicio ? dayjs(filters.fechaInicio) : null,
                  filters.fechaFin ? dayjs(filters.fechaFin) : null,
                ]}
                onChange={handleDateRangeChange}
                format="DD/MM/YYYY"
                style={{ width: "100%" }}
                placeholder={["Fecha inicio", "Fecha fin"]}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select
                placeholder="Transportadora"
                value={filters.transportadora_id}
                onChange={(value) => handleFilterChange("transportadora_id", value)}
                allowClear
                style={{ width: "100%" }}
                options={transportadoras.map((t) => ({
                  value: t.id,
                  label: t.nombre,
                }))}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select
                placeholder="Estado"
                value={filters.estado}
                onChange={(value) => handleFilterChange("estado", value)}
                allowClear
                style={{ width: "100%" }}
                options={Object.keys(ESTADO_COLORS).map((estado) => ({
                  value: estado,
                  label: ESTADO_COLORS[estado].label,
                }))}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Input
                placeholder="Buscar por código, paciente..."
                prefix={<SearchOutlined />}
                value={filters.searchQuery}
                onChange={(e) => handleFilterChange("searchQuery", e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Space>
                <Button
                  type={viewMode === "cards" ? "primary" : "default"}
                  onClick={() => setViewMode("cards")}
                  size="small"
                >
                  Tarjetas
                </Button>
                <Button
                  type={viewMode === "table" ? "primary" : "default"}
                  onClick={() => setViewMode("table")}
                  size="small"
                >
                  Tabla
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Lista de planillas */}
        <Spin spinning={loading}>
          {viewMode === "cards" ? (
            <div className="planillas-list">
              {planillas.length === 0 ? (
                <Empty
                  description="No hay planillas para mostrar"
                  style={{ marginTop: 48 }}
                />
              ) : (
                <>
                  <div className="planillas-list-header">
                    <Space>
                      <Button
                        size="small"
                        onClick={() =>
                          handleSelectAll(selectedPlanillas.length !== planillas.length)
                        }
                      >
                        {selectedPlanillas.length === planillas.length
                          ? "Deseleccionar todo"
                          : "Seleccionar todo"}
                      </Button>
                      <Text type="secondary">
                        {selectedPlanillas.length} seleccionadas de {planillas.length}
                      </Text>
                    </Space>
                  </div>
                  {planillas.map((planilla) => (
                    <PlanillaTAICard
                      key={planilla.id}
                      planilla={planilla}
                      isSelected={selectedPlanillas.includes(planilla.id)}
                      onSelect={(selected) => handleSelectPlanilla(planilla.id, selected)}
                      onCambiarEstado={() => handleOpenEstadoModal(planilla)}
                      hasPermission={hasPermission}
                    />
                  ))}
                </>
              )}
            </div>
          ) : (
            <Card bodyStyle={{ padding: 0 }}>
              <Table
                dataSource={planillas}
                columns={tableColumns}
                rowKey="id"
                pagination={{
                  ...pagination,
                  showSizeChanger: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} de ${total} planillas`,
                  onChange: (page, pageSize) => {
                    setPagination({ ...pagination, current: page, pageSize });
                  },
                }}
                rowSelection={{
                  selectedRowKeys: selectedPlanillas,
                  onChange: setSelectedPlanillas,
                }}
                scroll={{ x: 1200 }}
                size="small"
              />
            </Card>
          )}
        </Spin>

        {/* Modal para cambiar estado */}
        {selectedPlanillaEstado && (
          <PlanillaTAIEstadoModal
            open={estadoModalOpen}
            onClose={() => {
              setEstadoModalOpen(false);
              setSelectedPlanillaEstado(null);
            }}
            planilla={selectedPlanillaEstado}
            onSuccess={handleEstadoSuccess}
          />
        )}
      </Content>
    </Layout>
  );
};

export default PlanillasTAI;
